import { type ReactNode } from 'react';

export type Faq = {
  id: string;
  question: string;
  answer: ReactNode;
};

/**
 * The gifting FAQs, shown on both the confirmation screen (GIFTWEB-30 AC 8)
 * and About Us.
 *
 * One list, two pages, deliberately: they used to carry separate copies that
 * had drifted apart — About Us still named BlackRock as the fund manager after
 * the confirmation copy had moved on — and this is regulated promotional copy,
 * so the two pages disagreeing is a problem rather than an untidiness.
 *
 * Answers are bare paragraphs with no wrapper: each page supplies its own
 * (`FaqAnswer` on the confirmation screen, a styled `div` on About Us), so
 * nothing here assumes one page's type scale.
 */
export const GIFTING_FAQS: readonly Faq[] = [
  {
    id: 'track-gifts',
    question: 'Can I track my gifts?',
    answer: (
      <>
        <p>
          Yes! We’ve listened to parents, grandparents, aunties, uncles and
          friends who told us they wanted to feel more connected to the children
          whose futures they’re helping to build.
        </p>
        <p className='mt-2'>
          In the Mia app, you can see all the children you’re investing or
          gifting for in one place. So if you’re a parent, you can see your own
          children alongside any other children you gift to. And if you’re a
          grandparent, auntie, uncle or friend, you can see all the children you
          gift to together, along with your gifting history.
        </p>
        <p className='mt-2'>
          You can also leave voice, video or text and photo messages with your
          gifts for each child to unlock when they’re older - so one day, they
          can see not just what was given, but the people and memories behind
          it.
        </p>
      </>
    ),
  },
  {
    id: 'who-gifted',
    question: 'Will the child know who gifted?',
    answer: (
      <p>
        Yes! When a child turns 18, they’ll be able to see the family and
        friends who helped build their future through contributions to their
        Junior Stocks &amp; Shares <abbr>ISA</abbr> and Junior Pension. They’ll
        also be able to look back at the text, voice and video messages saved
        for them in their Memory Box (subject to parental approval and in-app
        upgrade) - so they can see not just what was given, but the people and
        memories behind it too.
      </p>
    ),
  },
  {
    id: 'is-it-safe',
    question: 'Is my gift safe?',
    answer: (
      <>
        <p>
          Yes. All cash and investments are held securely in a ring-fenced
          account with Seccl, our <abbr>FCA</abbr>-regulated investment platform
          provider. Seccl is part of the Octopus Group, which manages over £12bn
          in assets and also works with companies like Monzo and GoHenry. Client
          money and investments are required to be held separately from Mia
          Wealth&apos;s and Seccl’s own assets, so the expectation is that
          customers would remain entitled to the full value of their investments
          even in the event of insolvency.
        </p>
        <p className='mt-2'>
          Children&apos;s investments are managed independently by some of the
          world’s largest asset managers (Aviva &amp; Fidelity). In practice, if
          Mia Wealth stopped operating, the investments would continue to exist
          and would typically be transferred to another regulated provider. If
          the exact same investment fund wasn’t available on the new platform,
          their investments would usually be moved into a similar fund - this is
          standard practice whenever an account is transferred between
          providers.
        </p>
        <p className='mt-2'>
          Children&apos;s investments are also supported by the Financial
          Services Compensation Scheme (
          <abbr title='Financial Services Compensation Scheme'>FSCS</abbr>). If
          Mia Wealth or Seccl were to fail and there was a shortfall in client
          assets, eligible investment claims are generally protected up to
          £85,000 per person, per firm. This does not cover normal investment
          market movements or losses.
        </p>
        <p className='mt-2'>
          Mia Wealth Limited (Mia Wealth) is an appointed representative of
          RiskSave Technologies Ltd, which is authorised and regulated by the
          Financial Conduct Authority (
          <abbr title='Firm Reference Number'>FRN</abbr> 775330). Mia Wealth is
          a company registered in England and Wales (No. 15818371). Mia Wealth
          can be found on the Financial Conduct Authority Financial Services
          register under <abbr title='Firm Reference Number'>FRN</abbr> 1033918.
        </p>
      </>
    ),
  },
  {
    id: 'one-off-or-regular',
    question: 'Can I make a one-off gift or gift regularly each month?',
    answer: (
      <p>
        It’s completely up to you. Parents can make one-off contributions or set
        up regular contributions from £20 a month, making it easy to build
        investing into the monthly routine. Family and friends can make one-off
        gifts from just £10 whenever they like. Regular contributions for
        gifters are coming soon, so they’ll also be able to set up an amount to
        contribute automatically.
      </p>
    ),
  },
  {
    id: 'cost-to-gift',
    question: 'Is there a cost to gift?',
    answer: (
      <p>
        No - there’s no fee for family and friends to make a gift. Gifts start
        from just £10. After their first gift, they can download the Mia app for
        free to see all the children they gift to in one place, keep track of
        their gifting history and easily contribute again whenever they want.
      </p>
    ),
  },
];
